import { useState, useMemo } from 'react';
import {
  ExternalAccount,
  TransferFundsFlow,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';
import { Dinero, dinero, toSnapshot } from 'dinero.js';

import CurrencyInput from 'components/generic-components/currencyInput';
import LoadingSpinner from 'icons/LoadingSpinner';
import Button from 'components/app-components/ui/button';
import { getDineroCurrency } from 'utils/currency';
import useIntl from 'hooks/useIntl';
import { useUser } from 'context/user';
import { useBridgeGetWallet } from 'hooks/useBridgeSWR';
import { useTheraFernClient } from 'hooks/useFern';
import { formatErrorMessage } from 'utils/error';
import theraToast from 'components/generic-components/toast/TheraToast';
import InfoCircle from 'icons/InfoCircle';
import { useModal } from 'hooks/useModal';
import ExchangeDetails from '../modals/ExchangeDetailsModal';
import { isSupportAdminMode } from 'utils/user';
import useReadOnlyContractor from 'hooks/useOceansContractorReadOnly';
import { isCurrencySupportedOnChain, getSupportedCurrenciesForChain } from 'lib/bridgeConstants';

interface IPreviewAmount {
  feeCurrency: string;
  flatFee: number;
  variableFee: number;
  exchangeRate: number;
  exchangeFrom: number;
  exchangeTo: number;
}

const WalletBalanceAndInput = ({ selectedMethod }: { selectedMethod: ExternalAccount | null }) => {
  const { user, isLoading: isUserLoading, currentCompany } = useUser();
  const { theraFernApi } = useTheraFernClient();
  const { formatCurrencyToLocale } = useIntl();
  // Read-only contractors cannot withdraw from wallet
  const { isReadOnlyContractor, readOnlyTooltipMessage } = useReadOnlyContractor();
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withDrawalAmountError, setWithDrawalAmountError] = useState<string | null>(null);
  const { bridgeWallet, isPending: isBridgeWalletPending } = useBridgeGetWallet({
    walletId: user?.bridgeUserDetails?.wallet?.id,
  });

  // Show USDB by default; fall back to USDC only if USDB balance is 0 and USDC balance is > 0
  const usdbBalance = bridgeWallet?.balances?.find((b) => b?.currency === 'usdb');
  const usdcBalance = bridgeWallet?.balances?.find((b) => b?.currency === 'usdc');
  const nonZeroBalance =
    Number(usdbBalance?.balance ?? 0) === 0 && Number(usdcBalance?.balance ?? 0) > 0 ? usdcBalance : usdbBalance;
  const balance = nonZeroBalance?.balance ?? 0;
  const balanceFormatted = Number(Math.round(Number(balance) * 100));
  const balanceActualCurrency = (nonZeroBalance?.currency || 'USDB').toUpperCase();
  const balanceCurrency = 'USD';
  // amount to withdraw in selected method's currency
  const [amount, setAmount] = useState<number | null>();
  const [dineroAmount, setDineroAmount] = useState<Dinero<number> | null>(
    dinero({
      amount: amount ?? 0,
      currency: getDineroCurrency(balanceCurrency),
    }),
  );
  const [previewAmount, setPreviewAmount] = useState<IPreviewAmount | null>(null);
  const [isFetchingPreview, _setIsFetchingPreview] = useState(false);
  const dineroBalanceCurrency = getDineroCurrency(balanceCurrency);
  const withdrawalAmount = dineroAmount ? toSnapshot(dineroAmount).amount : 0;
  const balanceAmount = balanceFormatted;
  const isWithdrawingMoreThanAvailableBalance = withdrawalAmount > balanceAmount;
  const remainingBalance = isWithdrawingMoreThanAvailableBalance ? 0 : balanceAmount - withdrawalAmount;
  const withdrawalCurrency = selectedMethod?.currency;
  const exchangeDetailsModal = useModal();

  const resetBalanceInput = () => {
    setAmount(undefined);
    setDineroAmount((_prev) =>
      dinero({
        amount: 0,
        currency: dineroBalanceCurrency,
      }),
    );
  };

  // Source wallet chain from bridge wallet
  const sourceChain = bridgeWallet?.chain;
  // Destination payment rail from selected external account
  const destinationPaymentRail = selectedMethod?.paymentRail;

  const proceedToWithdraw = async () => {
    if (isWithdrawing) return;

    // Validate Bridge payment route restrictions before proceeding
    // Reference: https://apidocs.bridge.xyz/get-started/introduction/what-we-support/payment-routes

    // Check if the source chain supports the requested currency
    if (!isCurrencySupportedOnChain(sourceChain, withdrawalCurrency)) {
      const supportedSourceCurrencies = getSupportedCurrenciesForChain(sourceChain);
      const errorMessage = `Currency ${withdrawalCurrency?.toUpperCase()} is not supported on ${sourceChain?.toUpperCase() || 'source chain'}. Supported currencies: ${supportedSourceCurrencies.map((c) => c.toUpperCase()).join(', ') || 'None'}`;
      setWithDrawalAmountError(errorMessage);
      theraToast.error({
        message: errorMessage,
        options: {
          id: 'thera-wallet-unsupported-source-currency',
          sendToSlack: false,
        },
      });
      return;
    }

    // Check if the destination payment rail supports the requested currency
    if (!isCurrencySupportedOnChain(destinationPaymentRail, withdrawalCurrency)) {
      const supportedDestCurrencies = getSupportedCurrenciesForChain(destinationPaymentRail);
      const errorMessage = `Currency ${withdrawalCurrency?.toUpperCase()} is not supported on ${destinationPaymentRail?.toUpperCase() || 'destination chain'}. Supported currencies: ${supportedDestCurrencies.map((c) => c.toUpperCase()).join(', ') || 'None'}`;
      setWithDrawalAmountError(errorMessage);
      theraToast.error({
        message: errorMessage,
        options: {
          id: 'thera-wallet-unsupported-dest-currency',
          sendToSlack: false,
        },
      });
      return;
    }

    try {
      setIsWithdrawing(true);
      await theraFernApi.thera.bridge.bridgeApi.transferFunds(currentCompany?.companyID, {
        fromWalletId: user?.bridgeUserDetails?.wallet?.id,
        toWalletId: selectedMethod?.walletAddress,
        // convert withdrawal amount in numerical cents to string dollars
        amount: (withdrawalAmount / 100).toFixed(2),
        currency: withdrawalCurrency,
        flow: TransferFundsFlow.PayoutWallet,
      });
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to withdraw funds');
      setWithDrawalAmountError(errorMessage);
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'thera-wallet-withdraw-error',
        },
      });
      resetBalanceInput();
    } finally {
      setIsWithdrawing(false);
    }
  };

  const isWithDrawBtnDisabled = useMemo(() => {
    const isWithdrawingMoreThanAvailableBalance = withdrawalAmount > balanceAmount;
    return (
      withdrawalAmount === 0 ||
      isWithdrawing ||
      isBridgeWalletPending ||
      !selectedMethod ||
      isWithdrawingMoreThanAvailableBalance
    );
  }, [withdrawalAmount, isWithdrawing, isBridgeWalletPending, selectedMethod, balanceAmount]);

  const useFullBalance = () => {
    // Set the withdrawal amount to the available balance
    if (balanceAmount) {
      setAmount(balanceAmount);
      setDineroAmount((_prev) =>
        dinero({
          amount: balanceAmount,
          currency: dineroBalanceCurrency,
        }),
      );
    }
  };

  const handleAmountChange = async (value: number) => {
    setWithDrawalAmountError(null);
    const amount = value || 0;
    setAmount(amount);
    setPreviewAmount(null);
    setDineroAmount((_prev) =>
      dinero({
        amount,
        currency: dineroBalanceCurrency,
      }),
    );
    // if amount is more than available balance, set amount to available balance
    if (amount > 0) {
      if (amount > balanceAmount) {
        setWithDrawalAmountError('Withdrawal Amount is more than Available Balance');
      }
    }
  };

  return (
    <div className="relative rounded-lg border border-gray-200 p-4">
      <div className="mb-4 flex items-center justify-between">
        <p className="font-medium text-neutral-500">{balanceActualCurrency} Balance</p>
        <p role="button" className="text-sm text-therablue" onClick={useFullBalance}>
          Use Full Balance
        </p>
      </div>

      {isUserLoading ? (
        <div className="my-12 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="mb-4">
            <CurrencyInput
              placeholder="00.00"
              onChange={handleAmountChange}
              value={amount}
              currencyCode={balanceCurrency}
              showCurrencySymbol
              // Autofocus only on fine-pointer (desktop) devices — on touch it would
              // pop the mobile keyboard open the moment the page loads.
              autoFocus={typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches}
            />
          </div>
          {withDrawalAmountError && <p className="mb-4 text-xs text-red-500">{withDrawalAmountError}</p>}
          {isWithdrawingMoreThanAvailableBalance ? (
            <p className="font-neutral-800 text-sm">
              You can only withdraw up to
              <span className="font-bold text-neutral-900">
                {' '}
                {formatCurrencyToLocale(balanceAmount, balanceCurrency)}
              </span>
            </p>
          ) : (
            <p className="font-neutral-800 text-sm">
              Your remaining wallet balance in {balanceActualCurrency} would be
              <span className="font-bold text-neutral-900">
                {' '}
                {formatCurrencyToLocale(remainingBalance, balanceCurrency)}
              </span>
            </p>
          )}
          <div className="my-6 border-t border-gray-200 pt-6">
            <div>
              <p className="mb-1 text-sm font-medium text-neutral-600">Summary</p>
              <p className="mb-4 text-xs font-normal text-neutral-500">
                Estimated exchange details. A 1% platform fee will be charged for transfer during payout to your
                personal crypto wallet.
              </p>
            </div>
            {previewAmount ? (
              <>
                {(previewAmount?.flatFee || previewAmount?.flatFee > 0) && (
                  <p className="mt-2 flex items-center justify-between rounded-md bg-neutral-50 p-4 text-sm">
                    <span className="text-neutral-500">Provider Fee</span>
                    {isFetchingPreview ? (
                      <LoadingSpinner height={14} width={14} />
                    ) : (
                      <span className="font-medium text-neutral-800">
                        -{formatCurrencyToLocale(previewAmount?.flatFee, previewAmount?.feeCurrency)}
                      </span>
                    )}
                  </p>
                )}
                {previewAmount && (
                  <div className="mt-2 flex items-center justify-between rounded-md bg-neutral-50 p-4 text-sm">
                    <span className="text-neutral-500">Amount to send</span>
                    {isFetchingPreview ? (
                      <LoadingSpinner height={14} width={14} />
                    ) : (
                      <div className="text-right">
                        <p className="text-sm font-medium text-neutral-800">
                          {formatCurrencyToLocale(previewAmount.exchangeFrom, balanceCurrency)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {previewAmount && (
                  <div className="mt-2 flex items-center justify-between rounded-md bg-neutral-50 p-4 text-sm">
                    <span className="flex text-neutral-500">
                      Exchange to
                      {!isFetchingPreview && (
                        <div role="button" onClick={exchangeDetailsModal.handleOpen}>
                          <InfoCircle className="ml-2 w-4" />
                        </div>
                      )}
                    </span>
                    {isFetchingPreview ? (
                      <LoadingSpinner height={14} width={14} />
                    ) : (
                      <div className="text-right">
                        <p className="text-sm font-medium text-neutral-800">
                          {formatCurrencyToLocale(previewAmount.exchangeTo, withdrawalCurrency)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="mt-2 flex items-center justify-between rounded-md bg-neutral-50 p-4 text-sm">
                <span className="text-neutral-500">Withdrawal Amount</span>
                <span className="font-medium text-neutral-800">
                  {formatCurrencyToLocale(withdrawalAmount, balanceCurrency)}
                </span>
              </p>
            )}
          </div>

          {exchangeDetailsModal.isOpen && (
            <ExchangeDetails
              open={exchangeDetailsModal.isOpen}
              onClose={exchangeDetailsModal.handleClose}
              convertedAmount={previewAmount}
              balanceCurrency={balanceCurrency}
              withdrawalCurrency={withdrawalCurrency}
            />
          )}

          <Button
            className="w-full"
            onClick={proceedToWithdraw}
            loading={isWithdrawing || isFetchingPreview}
            disabled={isWithDrawBtnDisabled || isSupportAdminMode || isReadOnlyContractor}
            tooltipContent={isReadOnlyContractor ? readOnlyTooltipMessage : null}
            themeOfTooltip={{ target: 'w-full' }}
          >
            Withdraw
          </Button>

          <p className="mt-4 flex items-center text-xs text-neutral-500">
            <InfoCircle className="mr-2 w-4" />
            Withdrawals to crypto are irreversible. Please double check the amount and wallet address. A 1% platform fee
            will be charged for this transfer.
          </p>
        </>
      )}
    </div>
  );
};

export default WalletBalanceAndInput;
